/**
 * Following a payee-naming run (TMMAPP-0163 item 3; TMMAPI-0193 U1). On mount it reads
 * `runs/current`, so a run started in another tab or before a reload shows; a run that has already
 * ended there is not news and isn't shown. While the run is `RUNNING` it reads `runs/{runId}` every
 * 3 s, never while the tab is hidden, and stops when the run ends or the host unmounts. A failed
 * read keeps the last status and the next tick tries again. Reads never overlap (TMMAPP-0164 item 2):
 * while one is out nothing is scheduled, and its settling schedules the next.
 *
 * `onProposed` fires when a read shows `proposed` grew, so the host re-reads the review list in
 * place; `onEnded` fires once when a run it watched ends. A read fires at most one of them.
 * Nothing here starts a run: `follow` takes the id the person's own Continue started.
 */
import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { payeeSuggestionsApi, type PayeeNamingRun } from '@/api/payeeSuggestions';

export const NAMING_RUN_POLL_MS = 3000;

function subscribeVisibility(onChange: () => void): () => void {
  document.addEventListener('visibilitychange', onChange);
  return () => document.removeEventListener('visibilitychange', onChange);
}

function tabVisible(): boolean {
  return document.visibilityState !== 'hidden';
}

export interface NamingRunWatch {
  /** The run being shown: running, or ended while this host watched it. */
  run: PayeeNamingRun | null;
  /** Follows a run the person just started (or the one in flight that their start answered). */
  follow: (runId: number, total: number) => void;
  /** Stops the run (`…/cancel`); the answer is shown as the run's end. */
  cancel: () => Promise<void>;
  /** Clears an ended run's message. */
  clear: () => void;
}

export function useNamingRun({
  onProposed,
  onEnded,
}: {
  onProposed: () => void;
  onEnded: (run: PayeeNamingRun) => void;
}): NamingRunWatch {
  const [run, setRun] = useState<PayeeNamingRun | null>(null);
  // Bumped when a read settles, so the next tick is scheduled although the run didn't change.
  const [settled, setSettled] = useState(0);
  // A status read is out: no other is scheduled or sent until it settles.
  const inFlight = useRef(false);
  const visible = useSyncExternalStore(subscribeVisibility, tabVisible, () => true);
  const live = useRef(true);
  // The run on screen, for the reads' answers (state lags a render behind).
  const shown = useRef<PayeeNamingRun | null>(null);
  const callbacks = useRef({ onProposed, onEnded });
  useEffect(() => {
    callbacks.current = { onProposed, onEnded };
  });
  useEffect(() => {
    live.current = true;
    return () => {
      live.current = false;
    };
  }, []);

  const show = useCallback((next: PayeeNamingRun | null) => {
    shown.current = next;
    setRun(next);
  }, []);

  /** A read's answer: a late one for another run, or after the end, is dropped. */
  const apply = useCallback(
    (next: PayeeNamingRun) => {
      const prev = shown.current;
      if (!live.current || prev === null || prev.runId !== next.runId) return;
      if (prev.state !== 'RUNNING') return;
      show(next);
      // One call per read: an end re-reads everything, so it doesn't also say what grew.
      if (next.state !== 'RUNNING') callbacks.current.onEnded(next);
      else if (next.proposed > prev.proposed) callbacks.current.onProposed();
    },
    [show],
  );

  // A run already in flight shows on arrival.
  useEffect(() => {
    void payeeSuggestionsApi
      .currentRun()
      .then((current) => {
        if (!live.current || current === null || current.state !== 'RUNNING') return;
        if (shown.current === null) show(current);
      })
      .catch(() => {
        /* no run to show */
      });
  }, [show]);

  /** One status read; a failed one keeps the last status, and the next tick tries again. */
  const read = useCallback(
    (id: number) => {
      if (inFlight.current) return;
      inFlight.current = true;
      void payeeSuggestionsApi
        .getRun(id)
        .then(apply)
        .catch(() => {
          /* the next tick reads it again */
        })
        .finally(() => {
          inFlight.current = false;
          if (live.current) setSettled((n) => n + 1);
        });
    },
    [apply],
  );

  const running = run !== null && run.state === 'RUNNING';
  const runId = run?.runId ?? null;
  useEffect(() => {
    // A read already out schedules the next when it settles (`settled`).
    if (!running || runId === null || !visible || inFlight.current) return;
    const handle = window.setTimeout(() => read(runId), NAMING_RUN_POLL_MS);
    return () => window.clearTimeout(handle);
  }, [running, runId, visible, run, settled, read]);

  const follow = useCallback(
    (id: number, total: number) => {
      // The run already on screen (a start answers the run in flight) keeps its numbers.
      if (shown.current?.runId === id && shown.current.state === 'RUNNING') return;
      // The run shows at once; its first read replaces this placeholder.
      show({
        runId: id,
        state: 'RUNNING',
        stopReason: null,
        total,
        evaluated: 0,
        proposed: 0,
        calls: 0,
      });
      read(id);
    },
    [show, read],
  );

  const cancel = useCallback(async () => {
    const current = shown.current;
    if (current === null || current.state !== 'RUNNING') return;
    apply(await payeeSuggestionsApi.cancelRun(current.runId));
  }, [apply]);

  const clear = useCallback(() => {
    if (shown.current !== null && shown.current.state !== 'RUNNING') show(null);
  }, [show]);

  return { run, follow, cancel, clear };
}
